import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronRight, Calculator, Smartphone, ShieldCheck, ArrowUp } from 'lucide-react';
import { DentosHeader } from '../components/DentosHeader';
import { DentosFooter } from '../components/DentosFooter';
interface DentosLandingProps {
  onNavigate: (page: 'home' | 'knowledge' | 'tools' | 'research' | 'dental-kids' | 'logo') => void;
}
export function DentosLanding({
  onNavigate
}: DentosLandingProps) {
  // Calculator State
  const [chairs, setChairs] = useState(6);
  const [avgCheck, setAvgCheck] = useState(35000);
  const [noShowRate, setNoShowRate] = useState(18);
  // Calculator Results
  const [monthlyLoss, setMonthlyLoss] = useState(0);
  const [yearlyLoss, setYearlyLoss] = useState(0);
  const [lostFamilies, setLostFamilies] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    // Formula: chairs × 20 working days × 4 patients/day × avgCheck × noShowRate
    const monthly = chairs * 20 * 4 * avgCheck * (noShowRate / 100);
    const yearly = monthly * 12;
    const families = Math.round(yearly / 35000);
    setMonthlyLoss(monthly);
    setYearlyLoss(yearly);
    setLostFamilies(families);
  }, [chairs, avgCheck, noShowRate]);
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const formatCurrency = (val: number) => {
    return val.toLocaleString('ru-KZ') + ' ₸';
  };
  return <div className="min-h-screen font-sans text-slate-800" data-id="element-279">
      <DentosHeader currentPage="home" onNavigate={onNavigate} data-id="element-280" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 bg-gradient-to-br from-[#0F1E3C] to-[#00C2D4] overflow-hidden" data-id="element-281">
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-282">
          <div className="max-w-4xl" data-id="element-283">
            <span className="inline-block text-[#00C2D4] font-semibold tracking-wider mb-4 uppercase text-sm md:text-base bg-[#0F1E3C]/30 px-3 py-1 rounded-full backdrop-blur-sm" data-id="element-284">
              Платформа для владельцев стоматологий Казахстана
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6" data-id="element-285">
              Не всё измеримое важно.
              <br data-id="element-286" />
              <span className="text-white/90" data-id="element-287">Не всё важное — измеримо.</span>
            </h1>
            <p className="text-lg md:text-xl text-[#A0AEC0] max-w-2xl mb-10 leading-relaxed" data-id="element-288">
              Пока вы тратите бюджет на новых пациентов — ваши конкуренты
              забирают тех кто уже вам доверял. Не потому что лечат лучше. А
              потому что чаще напоминают о себе. DENTOS создан для тех кто
              считает правильные цифры.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-289">
              <button onClick={() => {
              onNavigate('knowledge');
              window.scrollTo(0, 0);
            }} className="bg-[#00C2D4] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00acc1] transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#00C2D4]/30 flex items-center justify-center" data-id="element-290">
                Перейти в базу знаний
              </button>
              <button onClick={() => {
              onNavigate('dental-kids');
              window.scrollTo(0, 0);
            }} className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-all hover:scale-105 flex items-center justify-center" data-id="element-291">
                Узнать о Dental Kids
              </button>
            </div>
          </div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#00C2D4]/10 to-transparent pointer-events-none" data-id="element-292"></div>
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-293"></div>
      </section>

      {/* PAIN BLOCK & CALCULATOR */}
      <section className="py-20 bg-white" data-id="element-294">
        <div className="container mx-auto px-4 md:px-6" data-id="element-295">
          <div className="max-w-3xl mb-16" data-id="element-296">
            <span className="text-[#00C2D4] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-297">
              РЕАЛЬНОСТЬ РЫНКА
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1E3C] mb-4" data-id="element-298">
              Вы лечите хорошо. Но деньги всё равно утекают.
            </h2>
            <p className="text-xl text-gray-600" data-id="element-299">
              Не из-за качества. Не из-за цен. А из-за того что происходит после
              того как пациент вышел за дверь.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start" data-id="element-300">
            {/* Left Column: Questions & Stats */}
            <div className="space-y-12" data-id="element-301">
              <div className="space-y-6" data-id="element-302">
                <div className="flex gap-4 items-start" data-id="element-303">
                  <span className="text-2xl" data-id="element-304">❓</span>
                  <h3 className="text-xl font-semibold text-[#0F1E3C]" data-id="element-305">
                    Сколько пациентов не вернулось к вам за последние 6 месяцев?
                  </h3>
                </div>
                <div className="flex gap-4 items-start" data-id="element-306">
                  <span className="text-2xl" data-id="element-307">❓</span>
                  <h3 className="text-xl font-semibold text-[#0F1E3C]" data-id="element-308">
                    Сколько денег вы потеряли на неявках в этом месяце?
                  </h3>
                </div>
                <div className="flex gap-4 items-start" data-id="element-309">
                  <span className="text-2xl" data-id="element-310">❓</span>
                  <h3 className="text-xl font-semibold text-[#0F1E3C]" data-id="element-311">
                    Когда последний раз ваш пациент вспомнил о вас сам — без
                    звонка и без рекламы?
                  </h3>
                </div>
              </div>

              <div className="grid gap-4" data-id="element-312">
                <div className="bg-[#0F1E3C] p-6 rounded-xl text-white hover:-translate-y-1 transition-transform duration-300 shadow-lg" data-id="element-313">
                  <div className="text-3xl font-bold text-[#00C2D4] mb-2" data-id="element-314">
                    363 дня
                  </div>
                  <p className="text-gray-300" data-id="element-315">
                    Столько дней в году пациент не думает о вашей клинике
                  </p>
                </div>
                <div className="bg-[#0F1E3C] p-6 rounded-xl text-white hover:-translate-y-1 transition-transform duration-300 shadow-lg" data-id="element-316">
                  <div className="text-3xl font-bold text-[#00C2D4] mb-2" data-id="element-317">
                    15-20%
                  </div>
                  <p className="text-gray-300" data-id="element-318">
                    Средний процент неявок. Деньги которые вы уже заработали —
                    но не получили
                  </p>
                </div>
                <div className="bg-[#0F1E3C] p-6 rounded-xl text-white hover:-translate-y-1 transition-transform duration-300 shadow-lg" data-id="element-319">
                  <div className="text-3xl font-bold text-[#00C2D4] mb-2" data-id="element-320">
                    ×3
                  </div>
                  <p className="text-gray-300" data-id="element-321">
                    Во столько раз дороже привлечь нового пациента чем удержать
                    существующего
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Calculator */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 md:p-8 sticky top-24" data-id="element-322">
              <div className="flex items-center gap-3 mb-6" data-id="element-323">
                <Calculator className="text-[#00C2D4]" size={28} data-id="element-324" />
                <h3 className="text-xl font-bold text-[#0F1E3C]" data-id="element-325">
                  Посчитайте сколько вы теряете прямо сейчас
                </h3>
              </div>

              <div className="space-y-8 mb-8" data-id="element-326">
                {/* Slider 1 */}
                <div data-id="element-327">
                  <div className="flex justify-between mb-2" data-id="element-328">
                    <label className="font-medium text-gray-700" data-id="element-329">
                      Кресел в клинике
                    </label>
                    <span className="font-bold text-[#0F1E3C]" data-id="element-330">{chairs}</span>
                  </div>
                  <input type="range" min="1" max="20" value={chairs} onChange={e => setChairs(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00C2D4]" data-id="element-331" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1" data-id="element-332">
                    <span data-id="element-333">1</span>
                    <span data-id="element-334">20</span>
                  </div>
                </div>

                {/* Slider 2 */}
                <div data-id="element-335">
                  <div className="flex justify-between mb-2" data-id="element-336">
                    <label className="font-medium text-gray-700" data-id="element-337">
                      Средний чек
                    </label>
                    <span className="font-bold text-[#0F1E3C]" data-id="element-338">
                      {avgCheck.toLocaleString('ru-KZ')} ₸
                    </span>
                  </div>
                  <input type="range" min="10000" max="100000" step="1000" value={avgCheck} onChange={e => setAvgCheck(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00C2D4]" data-id="element-339" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1" data-id="element-340">
                    <span data-id="element-341">10 000 ₸</span>
                    <span data-id="element-342">100 000 ₸</span>
                  </div>
                </div>

                {/* Slider 3 */}
                <div data-id="element-343">
                  <div className="flex justify-between mb-2" data-id="element-344">
                    <label className="font-medium text-gray-700" data-id="element-345">
                      Процент неявок
                    </label>
                    <span className="font-bold text-[#0F1E3C]" data-id="element-346">
                      {noShowRate}%
                    </span>
                  </div>
                  <input type="range" min="5" max="40" value={noShowRate} onChange={e => setNoShowRate(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00C2D4]" data-id="element-347" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1" data-id="element-348">
                    <span data-id="element-349">5%</span>
                    <span data-id="element-350">40%</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#F4F6F9] rounded-xl p-6 space-y-4 mb-6" data-id="element-351">
                <div data-id="element-352">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-353">
                    💸 Потери от неявок в месяц
                  </p>
                  <p className="text-2xl font-bold text-[#00C2D4]" data-id="element-354">
                    {formatCurrency(monthlyLoss)}
                  </p>
                </div>
                <div className="h-px bg-gray-200" data-id="element-355"></div>
                <div data-id="element-356">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-357">📅 Потери за год</p>
                  <p className="text-2xl font-bold text-[#0F1E3C]" data-id="element-358">
                    {formatCurrency(yearlyLoss)}
                  </p>
                </div>
                <div className="h-px bg-gray-200" data-id="element-359"></div>
                <div data-id="element-360">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-361">
                    👨‍👩‍👧 Потерянных семей в год
                  </p>
                  <p className="text-2xl font-bold text-[#0F1E3C]" data-id="element-362">
                    {lostFamilies.toLocaleString('ru-KZ')}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-500 italic mb-6" data-id="element-363">
                И это только потери от неявок. Без учёта пациентов которые
                просто ушли и не вернулись.
              </p>

              <div className="text-center" data-id="element-364">
                <p className="font-medium text-[#0F1E3C] mb-4" data-id="element-365">
                  Это не проблема маркетинга. Это проблема частоты контакта. И
                  именно это решает DENTOS.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION BLOCK */}
      <section className="py-20 bg-[#F4F6F9]" data-id="element-366">
        <div className="container mx-auto px-4 md:px-6" data-id="element-367">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-368">
            <span className="text-[#00C2D4] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-369">
              ЧТО ВНУТРИ ПЛАТФОРМЫ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1E3C]" data-id="element-370">
              Всё что нужно для роста — в одном месте
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-371">
            {[{
            icon: <ShieldCheck className="text-[#00C2D4]" size={32} data-id="element-372" />,
            title: 'База знаний',
            desc: 'Структурированные гайды по управлению, маркетингу и финансам. Без воды — только практика.'
          }, {
            icon: <Calculator className="text-[#00C2D4]" size={32} data-id="element-373" />,
            title: 'Инструменты',
            desc: 'Калькуляторы LTV, no-show и окупаемости. Считай свои цифры прямо на сайте.'
          }, {
            icon: <Smartphone className="text-[#00C2D4]" size={32} data-id="element-374" />,
            title: 'Dental Kids',
            desc: 'Мобильное приложение которое превращает редкий визит в ежедневную привычку.'
          }].map((item, idx) => <div key={idx} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300" data-id="element-375">
                <div className="mb-6 bg-[#F4F6F9] w-16 h-16 rounded-full flex items-center justify-center" data-id="element-376">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0F1E3C] mb-3" data-id="element-377">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-378">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* WHY DENTOS */}
      <section className="py-20 bg-white" data-id="element-379">
        <div className="container mx-auto px-4 md:px-6" data-id="element-380">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-381">
            <span className="text-[#00C2D4] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-382">
              ПОЧЕМУ DENTOS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1E3C]" data-id="element-383">
              Не просто платформа. Система роста для вашей клиники.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-384">
            {[{
            title: 'Только для стоматологии',
            desc: 'Никакой общей медицины. Каждая статья, каждый инструмент — написан под реальность стоматологического бизнеса Казахстана.'
          }, {
            title: 'Создано для казахстанского рынка',
            desc: 'Учитываем Kaspi Pay, местное законодательство и реалии рынка Астаны и Алматы. Не западные кейсы которые здесь не работают.'
          }, {
            title: 'Инструменты которые считают деньги',
            desc: 'Не теория — а калькуляторы которые показывают реальные цифры вашей клиники прямо сейчас.'
          }, {
            title: 'Реальные кейсы — не выдуманные истории',
            desc: 'Только проверенные кейсы из казахстанских клиник. Что сделали, что изменили, какой результат получили.'
          }, {
            title: 'Комьюнити владельцев',
            desc: 'Закрытое сообщество где обсуждают реальные проблемы без лишних глаз и конкурентов.'
          }, {
            title: 'Доступ к Dental Kids',
            desc: 'Единственная платформа где можно узнать о продукте который превращает редкий визит в ежедневную привычку.'
          }].map((item, idx) => <div key={idx} className="bg-[#F4F6F9] p-8 rounded-xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-id="element-385">
                <div className="flex items-start gap-4" data-id="element-386">
                  <div className="mt-1 text-2xl" data-id="element-387">
                    {['🎯', '🇰🇿', '🧮', '📊', '👥', '📱'][idx]}
                  </div>
                  <div data-id="element-388">
                    <h3 className="text-lg font-bold text-[#0F1E3C] mb-2" data-id="element-389">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-390">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* KNOWLEDGE BASE */}
      <section id="knowledge" className="py-20 bg-white" data-id="element-391">
        <div className="container mx-auto px-4 md:px-6" data-id="element-392">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-393">
            <span className="text-[#00C2D4] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-394">
              БАЗА ЗНАНИЙ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1E3C] mb-4" data-id="element-395">
              Практические гайды которые внедряются завтра утром
            </h2>
            <p className="text-xl text-gray-600" data-id="element-396">
              Не статьи ради статей — а пошаговые инструкции для конкретных
              задач вашего бизнеса
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-397">
            {[{
            icon: '📉',
            title: 'Как перестать терять 30% выручки из-за слабого администратора',
            desc: 'Один человек на ресепшене может делать или ломать ваш бизнес.',
            tag: 'Управление'
          }, {
            icon: '💰',
            title: 'Как поднять LTV семьи с 70 000 до 500 000 тенге без новых пациентов',
            desc: 'Считаем реальную ценность одной семьи и выстраиваем систему которая удерживает её на годы вперёд.',
            tag: 'Финансы'
          }, {
            icon: '👶',
            title: 'Как превратить испуганного ребёнка в лояльного пациента на 15 лет',
            desc: 'Психология детского страха и конкретные приёмы которые меняют отношение ребёнка к врачу навсегда.',
            tag: 'Детская стоматология'
          }, {
            icon: '⚖️',
            title: 'Пенсионные выплаты через стоматологии: как не стать частью схемы',
            desc: 'После задержания 11 владельцев клиник — разбираем что нужно знать чтобы защитить свой бизнес.',
            tag: 'Законодательство'
          }].map((item, idx) => <div key={idx} onClick={() => {
            onNavigate('knowledge');
            window.scrollTo(0, 0);
          }} className="group bg-white border border-gray-100 p-8 rounded-xl shadow-sm hover:shadow-lg hover:border-[#00C2D4]/30 transition-all duration-300 cursor-pointer" data-id="element-398">
                <div className="flex justify-between items-start mb-4" data-id="element-399">
                  <span className="text-3xl" data-id="element-400">{item.icon}</span>
                  <span className="bg-[#F4F6F9] text-[#0F1E3C] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-401">
                    {item.tag}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#0F1E3C] mb-3 group-hover:text-[#00C2D4] transition-colors" data-id="element-402">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-403">{item.desc}</p>
              </div>)}
          </div>

          <div className="text-center" data-id="element-404">
            <button onClick={() => {
            onNavigate('knowledge');
            window.scrollTo(0, 0);
          }} className="bg-[#00C2D4] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00acc1] transition-all hover:scale-105 hover:shadow-lg inline-flex items-center" data-id="element-405">
              Перейти в базу знаний <ChevronRight size={20} className="ml-2" data-id="element-406" />
            </button>
          </div>
        </div>
      </section>

      {/* TOOLS SECTION */}
      <section id="tools" className="py-20 bg-[#F4F6F9]" data-id="element-407">
        <div className="container mx-auto px-4 md:px-6" data-id="element-408">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-409">
            <span className="text-[#00C2D4] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-410">
              ИНСТРУМЕНТЫ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F1E3C] mb-4" data-id="element-411">
              Ваша клиника под микроскопом — бесплатно
            </h2>
            <p className="text-xl text-gray-600" data-id="element-412">
              Введи цифры своей клиники и получи полный разбор бизнеса за 60
              секунд
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-413">
            {[{
            icon: '🔴',
            title: 'Калькулятор скрытых потерь',
            desc: 'Неявки, незаписанные пациенты, низкий LTV — три утечки денег которые есть в каждой клинике.'
          }, {
            icon: '📊',
            title: 'Диагностика здоровья бизнеса',
            desc: '15 вопросов о вашей клинике — полный разбор: где вы сильны, где теряете деньги.'
          }, {
            icon: '💰',
            title: 'Калькулятор потенциала роста',
            desc: 'Введи текущие показатели клиники — и узнай сколько ты можешь зарабатывать если исправишь только 3 метрики.'
          }, {
            icon: '📈',
            title: 'Сравнительный анализ с рынком',
            desc: 'Где ваша клиника относительно среднего по Казахстану? Сравни свои показатели.'
          }].map((tool, idx) => <div key={idx} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col" data-id="element-414">
                <div className="text-3xl mb-4" data-id="element-415">{tool.icon}</div>
                <h3 className="text-lg font-bold text-[#0F1E3C] mb-3" data-id="element-416">
                  {tool.title}
                </h3>
                <p className="text-gray-600 text-sm flex-grow mb-4" data-id="element-417">
                  {tool.desc}
                </p>
                <div className="text-[#00C2D4] font-bold text-sm flex items-center mt-auto" data-id="element-418">
                  Запустить <ChevronRight size={14} className="ml-1" data-id="element-419" />
                </div>
              </div>)}
          </div>

          <div className="text-center" data-id="element-420">
            <button onClick={() => {
            onNavigate('tools');
            window.scrollTo(0, 0);
          }} className="bg-white border-2 border-[#00C2D4] text-[#00C2D4] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00C2D4] hover:text-white transition-all hover:scale-105 inline-flex items-center" data-id="element-421">
              Попробовать инструменты бесплатно{' '}
              <ArrowRight size={20} className="ml-2" data-id="element-422" />
            </button>
          </div>
        </div>
      </section>

      {/* RESEARCH SECTION */}
      <section id="research" className="py-20 bg-white" data-id="element-423">
        <div className="container mx-auto px-4 md:px-6" data-id="element-424">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4" data-id="element-425">
            <div data-id="element-426">
              <span className="text-[#00C2D4] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-427">
                ИССЛЕДОВАНИЯ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F1E3C]" data-id="element-428">
                Цифры которые меняют взгляд на бизнес
              </h2>
            </div>
            <button onClick={() => {
            onNavigate('research');
            window.scrollTo(0, 0);
          }} className="text-[#00C2D4] font-bold flex items-center hover:underline" data-id="element-429">
              Смотреть все исследования{' '}
              <ArrowRight size={16} className="ml-2" data-id="element-430" />
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-431">
            {[{
            source: 'ВОЗ • 2022',
            icon: '🏛️',
            stat: '3.7 миллиарда человек страдают от болезней полости рта',
            desc: 'Стоматология — это не ниша. Это крупнейший нелеченый рынок в мире.'
          }, {
            source: 'Towards Healthcare • 2025',
            icon: '📊',
            stat: 'Рынок стоматологии вырастет до $135 млрд к 2035 году',
            desc: 'Ежегодный рост 11.84%. Казахстан внутри этого тренда.'
          }, {
            source: 'ВОЗ Bangkok Declaration • 2024',
            icon: '🌍',
            stat: 'Стоматология признана глобальным приоритетом здравоохранения',
            desc: 'Первое в истории глобальное совещание ВОЗ по здоровью полости рта.'
          }].map((item, idx) => <div key={idx} className="bg-[#F4F6F9] p-8 rounded-xl hover:shadow-lg transition-all duration-300" data-id="element-432">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4 uppercase tracking-wide" data-id="element-433">
                  <span data-id="element-434">{item.icon}</span>
                  <span data-id="element-435">{item.source}</span>
                </div>
                <h3 className="text-xl font-bold text-[#0F1E3C] mb-4 leading-snug" data-id="element-436">
                  {item.stat}
                </h3>
                <p className="text-gray-600" data-id="element-437">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* DENTAL KIDS */}
      <section id="kids" className="py-20 bg-gradient-to-r from-[#0F1E3C] to-[#00C2D4] text-white" data-id="element-438">
        <div className="container mx-auto px-4 md:px-6" data-id="element-439">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-440">
            <div data-id="element-441">
              <span className="text-[#00C2D4] bg-white/10 px-3 py-1 rounded-full font-bold tracking-wider uppercase text-sm mb-6 inline-block backdrop-blur-sm" data-id="element-442">
                DENTAL KIDS
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-443">
                Пока конкуренты борются за новых пациентов — ваши уже
                возвращаются сами
              </h2>
              <p className="text-xl text-white/80 mb-8" data-id="element-444">
                Мобильное приложение которое превращает редкий визит в
                ежедневную привычку
              </p>

              <div className="space-y-6 mb-8" data-id="element-445">
                <div className="flex items-center gap-4" data-id="element-446">
                  <div className="text-3xl font-bold text-[#00C2D4]" data-id="element-447">
                    730 раз/год
                  </div>
                  <div className="text-white/70" data-id="element-448">
                    Контактов с семьёй вместо 2
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-449">
                  <div className="text-3xl font-bold text-[#00C2D4]" data-id="element-450">5-7%</div>
                  <div className="text-white/70" data-id="element-451">Неявки вместо 15-20%</div>
                </div>
                <div className="flex items-center gap-4" data-id="element-452">
                  <div className="text-3xl font-bold text-[#00C2D4]" data-id="element-453">
                    10 дней
                  </div>
                  <div className="text-white/70" data-id="element-454">
                    До запуска в вашей клинике
                  </div>
                </div>
              </div>

              <p className="italic text-white/60 mb-8" data-id="element-455">
                Первая клиника в своём районе которая внедрит это — заберёт
                рынок.
              </p>

              <button onClick={() => {
              onNavigate('dental-kids');
              window.scrollTo(0, 0);
            }} className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#0F1E3C] transition-all hover:scale-105 inline-flex items-center" data-id="element-456">
                Узнать о Dental Kids <ArrowRight size={20} className="ml-2" data-id="element-457" />
              </button>
            </div>

            <div className="hidden lg:flex justify-center" data-id="element-458">
              {/* Abstract phone representation or graphic */}
              <div className="relative w-80 h-[500px] bg-[#0F1E3C] rounded-[3rem] border-8 border-gray-800 shadow-2xl flex flex-col items-center justify-center overflow-hidden" data-id="element-459">
                <div className="absolute top-0 w-40 h-6 bg-gray-800 rounded-b-xl z-10" data-id="element-460"></div>
                <div className="w-full h-full bg-gradient-to-b from-[#0F1E3C] to-[#00C2D4] opacity-50 flex items-center justify-center" data-id="element-461">
                  <Smartphone size={80} className="text-white opacity-50" data-id="element-462" />
                </div>
                <div className="absolute bottom-10 w-full px-6" data-id="element-463">
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-4" data-id="element-464">
                    <div className="h-2 w-1/2 bg-white/30 rounded mb-2" data-id="element-465"></div>
                    <div className="h-2 w-3/4 bg-white/20 rounded" data-id="element-466"></div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4" data-id="element-467">
                    <div className="h-2 w-1/2 bg-white/30 rounded mb-2" data-id="element-468"></div>
                    <div className="h-2 w-3/4 bg-white/20 rounded" data-id="element-469"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-[#F4F6F9]" data-id="element-470">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-4xl" data-id="element-471">
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F1E3C] mb-6" data-id="element-472">
            Рынок меняется. Меняйтесь вместе с ним.
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed" data-id="element-473">
            Стоматология в Казахстане растёт. Технологии меняют отрасль.
            Пациенты становятся требовательнее. DENTOS создан для тех кто хочет
            быть на шаг впереди — не догонять, а задавать темп.
          </p>
          <button onClick={() => {
          onNavigate('knowledge');
          window.scrollTo(0, 0);
        }} className="bg-[#00C2D4] text-white px-10 py-5 rounded-lg font-bold text-xl hover:bg-[#00acc1] transition-all hover:scale-105 hover:shadow-lg shadow-[#00C2D4]/30" data-id="element-474">
            Перейти в базу знаний
          </button>
        </div>
      </section>

      <DentosFooter onNavigate={onNavigate} data-id="element-475" />

      {/* Scroll to top */}
      {showScrollTop && <button onClick={() => window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })} className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110" style={{
      background: '#00C2D4'
    }} aria-label="Наверх" data-id="element-476">
          <ArrowUp size={20} color="#fff" data-id="element-477" />
        </button>}
    </div>;
}